import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { larBuilderApi } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface ReportabilityOption {
  label: string;
  value: string;
  next: string;
}

interface ReportabilityQuestion {
  id: string;
  question: string;
  helpText: string;
  options: ReportabilityOption[];
}

interface ChecklistField {
  number: number;
  name: string;
  description: string;
  category: string;
  validValues?: string;
  isExempt: boolean;
}

interface ChecklistResult {
  actionTaken: string;
  requiredFields: ChecklistField[];
  categories: { name: string; fieldCount: number }[];
  totalRequired: number;
}

// ── Tab definitions ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'reportability', label: 'Reportability Screener', icon: CheckCircleIcon },
  { id: 'checklist', label: 'Field Checklist', icon: ClipboardDocumentListIcon },
  { id: 'gmi', label: 'GMI Scripts', icon: ChatBubbleBottomCenterTextIcon },
] as const;

type TabId = typeof TABS[number]['id'];

// ── Action types for checklist ───────────────────────────────────────────────

const ACTION_TYPES = [
  { value: '1', label: 'Originated' },
  { value: '2', label: 'Approved Not Accepted' },
  { value: '3', label: 'Denied' },
  { value: '4', label: 'Withdrawn' },
  { value: '5', label: 'Incomplete' },
  { value: '6', label: 'Purchased' },
];

const LOAN_TYPES = [
  { value: '1', label: 'Conventional' },
  { value: '2', label: 'FHA' },
  { value: '3', label: 'VA' },
  { value: '4', label: 'USDA' },
];

const LOAN_PURPOSES = [
  { value: '1', label: 'Home Purchase' },
  { value: '2', label: 'Home Improvement' },
  { value: '31', label: 'Refinancing' },
  { value: '32', label: 'Cash-Out Refinancing' },
  { value: '4', label: 'Other Purpose' },
];

// ── Main Page ────────────────────────────────────────────────────────────────

export default function LarFieldGuidePage() {
  const [activeTab, setActiveTab] = useState<TabId>('reportability');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">LO Field Guide</h1>
        <p className="mt-1 text-sm text-gray-500">
          HMDA reportability screening, dynamic field checklists, and GMI collection guidance for loan officers.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'reportability' && <ReportabilityTab />}
      {activeTab === 'checklist' && <ChecklistTab />}
      {activeTab === 'gmi' && <GmiScriptsTab />}
    </div>
  );
}

// ── Reportability Screener ───────────────────────────────────────────────────

function ReportabilityTab() {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<{
    isReportable: boolean;
    explanation: string;
    answeredQuestions: { question: string; answer: string }[];
  } | null>(null);

  const { data: treeData } = useQuery({
    queryKey: ['field-guide'],
    queryFn: () => larBuilderApi.getFieldGuide(),
    select: (res) => res.data as { questions: ReportabilityQuestion[] },
  });

  const questions = treeData?.questions || [];

  // Determine which question to show next
  const getVisibleQuestions = (): ReportabilityQuestion[] => {
    const visible: ReportabilityQuestion[] = [];
    let currentId = 'dwelling_secured';

    while (currentId !== 'REPORTABLE' && currentId !== 'NOT_REPORTABLE') {
      const q = questions.find((t) => t.id === currentId);
      if (!q) break;
      visible.push(q);

      const answer = answers[currentId];
      if (!answer) break;

      const option = q.options.find((o) => o.value === answer);
      if (!option) break;
      currentId = option.next;
    }

    return visible;
  };

  const visibleQuestions = getVisibleQuestions();

  const handleAnswer = async (questionId: string, value: string) => {
    const updated = { ...answers, [questionId]: value };

    // Clear downstream answers when an upstream answer changes
    const questionIds = questions.map((q) => q.id);
    const idx = questionIds.indexOf(questionId);
    for (let i = idx + 1; i < questionIds.length; i++) {
      delete updated[questionIds[i]];
    }

    setAnswers(updated);
    setResult(null);

    // Check if we've reached a terminal node
    const q = questions.find((t) => t.id === questionId);
    const option = q?.options.find((o) => o.value === value);
    if (option?.next === 'REPORTABLE' || option?.next === 'NOT_REPORTABLE') {
      try {
        const res = await larBuilderApi.evaluateReportability(updated);
        setResult(res.data);
      } catch {
        // Silently handle — user can still see the tree
      }
    }
  };

  const handleReset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="rounded-lg bg-blue-50 p-4">
        <h3 className="text-sm font-medium text-blue-800">How to use</h3>
        <p className="mt-1 text-sm text-blue-700">
          Answer each question about the loan to determine if it is HMDA-reportable.
          The screener follows the CFPB Regulation C decision framework.
        </p>
      </div>

      {/* Questions */}
      {visibleQuestions.map((q, idx) => (
        <div
          key={q.id}
          className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
        >
          <div className="mb-1 text-xs font-medium text-gray-400 uppercase">
            Question {idx + 1}
          </div>
          <h3 className="text-base font-semibold text-gray-900">{q.question}</h3>
          <p className="mt-1 text-sm text-gray-500">{q.helpText}</p>

          <div className="mt-4 space-y-2">
            {q.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleAnswer(q.id, opt.value)}
                className={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left text-sm transition ${
                  answers[q.id] === opt.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span>{opt.label}</span>
                {answers[q.id] === opt.value && (
                  <ChevronRightIcon className="h-4 w-4 text-blue-500" />
                )}
              </button>
            ))}
          </div>
        </div>
      ))}

      {/* Result */}
      {result && (
        <div
          className={`rounded-lg border-2 p-5 ${
            result.isReportable
              ? 'border-green-300 bg-green-50'
              : 'border-red-300 bg-red-50'
          }`}
        >
          <div className="flex items-center gap-3">
            {result.isReportable ? (
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            ) : (
              <XCircleIcon className="h-8 w-8 text-red-600" />
            )}
            <div>
              <h3
                className={`text-lg font-bold ${
                  result.isReportable ? 'text-green-800' : 'text-red-800'
                }`}
              >
                {result.isReportable ? 'HMDA Reportable' : 'Not HMDA Reportable'}
              </h3>
            </div>
          </div>
          <p
            className={`mt-3 text-sm ${
              result.isReportable ? 'text-green-700' : 'text-red-700'
            }`}
          >
            {result.explanation}
          </p>

          {result.answeredQuestions.length > 0 && (
            <div className="mt-4 space-y-1">
              <h4
                className={`text-xs font-medium uppercase ${
                  result.isReportable ? 'text-green-600' : 'text-red-600'
                }`}
              >
                Decision Path
              </h4>
              {result.answeredQuestions.map((aq, i) => (
                <div key={i} className="text-sm text-gray-700">
                  <span className="font-medium">{aq.question}</span>{' '}
                  <span className="italic">{aq.answer}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Reset */}
      {Object.keys(answers).length > 0 && (
        <button
          onClick={handleReset}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Start Over
        </button>
      )}
    </div>
  );
}

// ── Field Checklist ──────────────────────────────────────────────────────────

function ChecklistTab() {
  const [actionTaken, setActionTaken] = useState('1');
  const [loanType, setLoanType] = useState('1');
  const [loanPurpose, setLoanPurpose] = useState('1');

  const { data: checklist, isLoading } = useQuery({
    queryKey: ['field-checklist', actionTaken, loanType, loanPurpose],
    queryFn: () =>
      larBuilderApi.getChecklist({ actionTaken, loanType, loanPurpose }),
    select: (res) => res.data as ChecklistResult,
  });

  // Group fields by category for display
  const grouped = checklist?.requiredFields.reduce<Record<string, ChecklistField[]>>(
    (acc, field) => {
      const cat = field.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(field);
      return acc;
    },
    {},
  ) || {};

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-medium text-gray-900">
          Select Loan Characteristics
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-500">
              Action Taken
            </label>
            <select
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {ACTION_TYPES.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">
              Loan Type
            </label>
            <select
              value={loanType}
              onChange={(e) => setLoanType(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {LOAN_TYPES.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500">
              Loan Purpose
            </label>
            <select
              value={loanPurpose}
              onChange={(e) => setLoanPurpose(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {LOAN_PURPOSES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary */}
      {checklist && (
        <div className="flex items-center gap-4 rounded-lg bg-blue-50 px-4 py-3">
          <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />
          <div>
            <span className="text-sm font-semibold text-blue-900">
              {checklist.totalRequired} required fields
            </span>
            <span className="ml-2 text-sm text-blue-700">
              across {checklist.categories.length} categories
            </span>
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          <span className="ml-2 text-sm">Loading checklist...</span>
        </div>
      )}

      {/* Field groups */}
      {Object.entries(grouped).map(([category, fields]) => (
        <div
          key={category}
          className="rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-800">
              {category}{' '}
              <span className="font-normal text-gray-500">
                ({fields.length} field{fields.length !== 1 ? 's' : ''})
              </span>
            </h3>
          </div>
          <div className="divide-y divide-gray-100">
            {fields.map((field) => (
              <div
                key={field.number}
                className={`px-4 py-3 ${
                  field.isExempt ? 'bg-gray-50 opacity-60' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-5 min-w-[2rem] items-center justify-center rounded bg-gray-200 px-1.5 text-xs font-mono font-medium text-gray-600">
                        #{field.number}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {field.name}
                      </span>
                      {field.isExempt && (
                        <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
                          Exempt
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {field.description}
                    </p>
                  </div>
                  {field.validValues && (
                    <span className="ml-4 text-xs text-gray-400 whitespace-nowrap">
                      Values: {field.validValues}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── GMI Collection Scripts ───────────────────────────────────────────────────

function GmiScriptsTab() {
  const { data: scripts, isLoading } = useQuery({
    queryKey: ['gmi-scripts'],
    queryFn: () => larBuilderApi.getGmiScripts(),
    select: (res) =>
      res.data as Record<
        string,
        { title: string; script: string; note: string }
      >,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-gray-400">
        <ArrowPathIcon className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm">Loading scripts...</span>
      </div>
    );
  }

  if (!scripts) return null;

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-lg bg-amber-50 p-4">
        <h3 className="text-sm font-medium text-amber-800">
          Government Monitoring Information (GMI)
        </h3>
        <p className="mt-1 text-sm text-amber-700">
          HMDA requires collection of applicant ethnicity, race, and sex. The method
          varies by application channel. Below are the CFPB-compliant scripts and
          guidance for each channel.
        </p>
      </div>

      {Object.entries(scripts).map(([key, script]) => (
        <div
          key={key}
          className="rounded-lg border border-gray-200 bg-white shadow-sm"
        >
          <div className="border-b border-gray-100 bg-gray-50 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-800">
              {script.title}
            </h3>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="rounded-md bg-gray-50 p-4">
              <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {script.script}
              </p>
            </div>
            <div className="flex items-start gap-2">
              <ChatBubbleBottomCenterTextIcon className="mt-0.5 h-4 w-4 text-blue-500 shrink-0" />
              <p className="text-xs text-gray-600">{script.note}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
